/**
 * Nepal income tax / TDS / VAT / SSF / PF / capital gains rates for
 * **Fiscal Year 2081/82** (Shrawan 1, 2081 → Ashad end, 2082 ≈ 2024-07-16 → 2025-07-16).
 *
 * Source: Finance Act 2081 (Jestha 15, 2081), IRD circulars, SSF schedule.
 *
 * **Verify before deploying for production tax filings.** Rates here reflect
 * the version published in the gazette as of 2024-07. Any subsequent IRD
 * notification supersedes — open a PR to add a new FY file rather than
 * mutating this one.
 *
 * Sources:
 *  - https://ird.gov.np  (Finance Act 2081)
 *  - https://mof.gov.np  (Budget Speech 2081/82)
 *  - https://ssf.gov.np  (SSF contribution schedule)
 */

import type { RatesSnapshot } from "../types.js";

export const RATES_FY_2081_82: RatesSnapshot = {
  fiscalYear: "2081/82",

  // Individual (unmarried) slabs.
  slabsSingle: [
    { from: 0, to: 500_000, rate: 0.01, label: "SST" },
    { from: 500_000, to: 700_000, rate: 0.10 },
    { from: 700_000, to: 1_000_000, rate: 0.20 },
    { from: 1_000_000, to: 2_000_000, rate: 0.30 },
    { from: 2_000_000, to: 5_000_000, rate: 0.36 },
    { from: 5_000_000, to: Infinity, rate: 0.39 },
  ],

  // Couple (married) slabs.
  slabsCouple: [
    { from: 0, to: 600_000, rate: 0.01, label: "SST" },
    { from: 600_000, to: 800_000, rate: 0.10 },
    { from: 800_000, to: 1_100_000, rate: 0.20 },
    { from: 1_100_000, to: 2_000_000, rate: 0.30 },
    { from: 2_000_000, to: 5_000_000, rate: 0.36 },
    { from: 5_000_000, to: Infinity, rate: 0.39 },
  ],

  // TDS rates by category. All as fractions.
  tds: {
    salary: 0,                          // Uses slabs (handled separately).
    rent: 0.10,                         // House/land to individual.
    "rent-vehicle": 0.015,              // Commercial vehicle, VAT-registered.
    "service-vat-registered": 0.015,    // Service to VAT-registered vendor.
    "service-non-vat": 0.15,            // Service to non-VAT vendor.
    "service-non-resident": 0.15,       // Service paid to non-resident.
    royalty: 0.15,
    "dividend-resident": 0.05,
    "dividend-non-resident": 0.05,
    "interest-bank-individual": 0.05,   // Bank interest to natural person — final.
    "interest-other": 0.15,
    commission: 0.15,
    lottery: 0.25,                      // Lottery / windfall gain.
    "meeting-allowance": 0.15,
    "aircraft-lease": 0.10,
    "reinsurance-non-resident": 0.015,
    "consumer-committee": 0.015,
    "exam-fee": 0.15,
  },

  vat: {
    rate: 0.13,
    thresholdGoods: 5_000_000,
    thresholdService: 3_000_000,
  },

  capitalGains: {
    "shares-listed-individual-short": 0.075,
    "shares-listed-individual-long": 0.05,
    "shares-listed-entity": 0.10,
    "shares-listed-promoter": 0.10,
    "shares-unlisted-individual": 0.10,
    "shares-unlisted-entity": 0.15,
    "land-individual-short": 0.075,
    "land-individual-long": 0.05,
    "land-entity": 0.015,
  },

  corporate: {
    standard: 0.25,
    "bank-financial": 0.30,
    insurance: 0.30,
    telecom: 0.30,
    petroleum: 0.30,
    "capital-market": 0.30,
    "special-industry": 0.20,
    export: 0.20,
    "tobacco-alcohol": 0.30,
  },

  ssf: {
    employee: 0.11,    // 11% of basic
    employer: 0.20,    // 20% of basic
    breakdown: {
      medical: 0.0322,
      accident: 0.0140,
      dependent: 0.0027,
      retirement: 0.2611,
    },
  },

  pf: {
    employee: 0.10,
    employer: 0.10,
  },

  deductionCaps: {
    retirement: 300_000,         // Non-SSF members
    retirementSsf: 500_000,      // SSF members
    lifeInsurance: 40_000,
    healthInsurance: 20_000,
    donation: 100_000,
    donationPercentOfTaxableIncome: 0.05,
  },

  sourceUrl: "https://ird.gov.np",
};
